// ── QRScannerCamera — live camera QR scanner component ───────────────────
import { useEffect, useRef, useState } from 'react';
import { startQRScanner, parseBeeLoveQR } from '../utils/qrScanner';

export default function QRScannerCamera({ onScan, onClose }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const scannerRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading|scanning|success|error
  const [error,  setError]  = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!videoRef.current || !canvasRef.current) return;
    startQRScanner(
      videoRef.current,
      canvasRef.current,
      (data) => {
        const parsed = parseBeeLoveQR(data);
        if (parsed) {
          scannerRef.current?.stop();
          setResult(parsed);
          setStatus('success');
          onScan?.(parsed);
        } else {
          setError('Invalid QR code — not a BeeLove ticket');
          setStatus('error');
          setTimeout(() => { setStatus('scanning'); setError(''); }, 2000);
        }
      },
      (err) => { setError(err); setStatus('error'); }
    ).then(scanner => {
      scannerRef.current = scanner;
      setStatus('scanning');
    });
    return () => scannerRef.current?.stop();
  }, [onScan]);

  const G = {
    gold:'#C9A84C', green:'#4EC994', red:'#E07070',
    bg:'rgba(0,0,0,0.95)', border:'rgba(255,255,255,0.15)',
  };

  return (
    <div style={{ position:'relative', background:G.bg, borderRadius:12, overflow:'hidden' }}>
      {/* Camera viewfinder */}
      <div style={{ position:'relative', aspectRatio:'1', background:'#000', maxHeight:320 }}>
        <video ref={videoRef} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} playsInline muted/>
        <canvas ref={canvasRef} style={{ display:'none' }}/>

        {/* Scanning overlay */}
        {status === 'scanning' && (
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
            <div style={{ width:200, height:200, border:`2px solid ${G.gold}`, borderRadius:12, position:'relative' }}>
              {/* Corner markers */}
              {[['0,0','top:0;left:0'],['0,1','top:0;right:0'],['1,0','bottom:0;left:0'],['1,1','bottom:0;right:0']].map(([k, pos]) => (
                <div key={k} style={{ position:'absolute', width:20, height:20, borderColor:G.gold, borderStyle:'solid', borderWidth: pos.includes('bottom') ? '0 0 3px 3px' : pos.includes('right') ? '3px 3px 0 0' : pos.includes('top,left') ? '3px 0 0 3px' : '3px 3px 0 0', ...Object.fromEntries(pos.split(';').map(p => p.split(':'))) }}/>
              ))}
              {/* Scan line animation */}
              <div style={{ position:'absolute', left:0, right:0, height:2, background:G.gold, top:'50%', animation:'scanline 1.5s ease-in-out infinite' }}/>
            </div>
          </div>
        )}

        {/* Success overlay */}
        {status === 'success' && result && (
          <div style={{ position:'absolute', inset:0, background:'rgba(78,201,148,0.9)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}>
            <div style={{ fontSize:'2.5rem' }}>✓</div>
            <div style={{ color:'white', fontWeight:700 }}>Ticket validated</div>
            <div style={{ color:'rgba(255,255,255,0.8)', fontSize:'0.75rem' }}>Event: {result.eventId}</div>
          </div>
        )}

        {/* Error overlay */}
        {status === 'error' && (
          <div style={{ position:'absolute', inset:0, background:'rgba(224,112,112,0.85)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}>
            <div style={{ fontSize:'2rem' }}>✗</div>
            <div style={{ color:'white', fontSize:'0.8rem', textAlign:'center', padding:'0 1rem' }}>{error}</div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ padding:'0.9rem', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.5)' }}>
          {status === 'loading' ? 'Starting camera...' : status === 'scanning' ? 'Point at QR code' : ''}
        </div>
        <button onClick={onClose}
          style={{ padding:'0.4rem 0.9rem', background:'transparent', border:`1px solid ${G.border}`, borderRadius:8, color:'rgba(255,255,255,0.6)', cursor:'pointer', fontSize:'0.72rem' }}>
          Close
        </button>
      </div>
      <style>{`@keyframes scanline { 0%,100%{top:10%} 50%{top:85%} }`}</style>
    </div>
  );
}
